import { useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface PlatformFlags {
  enableDebugAudioUpload: boolean;
}

const DEFAULT_FLAGS: PlatformFlags = {
  enableDebugAudioUpload: false,
};

let cached: PlatformFlags | null = null;
let inflight: Promise<PlatformFlags> | null = null;

async function fetchPlatformFlags(): Promise<PlatformFlags> {
  const token = localStorage.getItem('accessToken');
  if (!token) return DEFAULT_FLAGS;

  try {
    const response = await fetch(`${API_URL}/api/v1/platform/public-flags`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) return DEFAULT_FLAGS;
    const result = await response.json();
    if (!result?.success || !result?.data) return DEFAULT_FLAGS;
    return { ...DEFAULT_FLAGS, ...result.data };
  } catch {
    return DEFAULT_FLAGS;
  }
}

/**
 * Read-only platform-wide feature flags. Cached at module level so the network
 * call happens once per session — flags are global and rarely change.
 *
 * Returns DEFAULT_FLAGS while loading and on any failure (silent), so callers
 * can render a sensible default without dealing with loading/error states.
 */
export function usePlatformFlags(): PlatformFlags {
  const [flags, setFlags] = useState<PlatformFlags>(cached ?? DEFAULT_FLAGS);

  useEffect(() => {
    if (cached) return;
    if (!inflight) inflight = fetchPlatformFlags();
    let cancelled = false;
    inflight.then((value) => {
      cached = value;
      inflight = null;
      if (!cancelled) setFlags(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return flags;
}
